/**
 * A login form component used for district flows that link accounts
 *
 * Currently used for districts activation and Clever Library
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import {LabeledField} from "@khanacademy/wonder-blocks-labeled-field";
import {Spring, Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, HeadingMedium} from "@khanacademy/wonder-blocks-typography";
import {GoogleButton} from "@khan/auth-google";
import {
    getLoginErrorMessageByCode,
    PasswordTextField,
} from "@khan/auth-password";
import {authType} from "@khan/auth-shared";
import {redirect} from "@khan/urls";

interface Props {
    isCleverLibrary: boolean;
    isTeacher: boolean;
    cleverToken?: string;
    disableEmail?: boolean;
    defaultIdentifier?: string;
    continueUrl: string;
    onForgotPasswordClick: () => void;
    onLoginError?: () => void;
    handleLogin: (identifier: string, password: string) => Promise<any>;
    formTitle?: string;
    formMessage?: string;
    /**
     * Optional identifier to use for the dialog title.
     *
     * Usefult for announcing the dialog to Screen Readers, as it will be used
     * as the `aria-labelledby` attribute of the dialog.
     */
    titleId?: string;
}

const LinkAccountLoginForm = ({
    isCleverLibrary,
    isTeacher,
    onForgotPasswordClick,
    continueUrl,
    formTitle,
    formMessage,
    disableEmail,
    defaultIdentifier,
    cleverToken,
    titleId,
    handleLogin,
    onLoginError,
}: Props) => {
    const [identifier, setIdentifier] = useState(defaultIdentifier || "");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [ssoIsRedirecting, setSsoIsRedirecting] = useState(false);

    const handleGoogleLoginError = (message: string) => {
        setError(message);
    };

    const submitLogin = (e: React.SyntheticEvent) => {
        e.preventDefault();

        setSubmitting(true);

        handleLogin(identifier, password).catch((res) => {
            const errorMessage = getLoginErrorMessageByCode(res.message);

            onLoginError && onLoginError();

            setSubmitting(false);
            setError(errorMessage);
        });
    };

    return (
        <>
            <HeadingMedium id={titleId}>
                {formTitle ? formTitle : t`Log in to your existing account`}
            </HeadingMedium>
            {formMessage ? (
                <View>
                    <Strut size={spacing.small_12} />
                    <Body style={styles.formMessage}>{formMessage}</Body>
                    <Strut size={spacing.small_12} />
                </View>
            ) : (
                <Strut size={spacing.large_24} />
            )}
            <GoogleButton
                role={isTeacher ? "teacher" : "learner"}
                linkClever={isCleverLibrary}
                cleverTransferToken={cleverToken}
                disabled={submitting}
                continueUrl={continueUrl}
                onError={handleGoogleLoginError}
                onSuccess={(redirectTo) => {
                    redirect(redirectTo);
                    setSsoIsRedirecting(true);
                }}
                context={authType.LOGIN}
            />
            {error ? (
                <View>
                    <Strut size={spacing.small_12} />
                    <Body style={styles.error}>{error}</Body>
                </View>
            ) : (
                <Strut size={spacing.medium_16} />
            )}
            <form onSubmit={submitLogin}>
                <LabeledField
                    label={
                        isCleverLibrary ? t`School email` : t`Email or username`
                    }
                    errorMessage={error}
                    description={t`An email or username is required to log in. You can also log in with Google.`}
                    field={
                        <TextField
                            type="text"
                            testId="identifier"
                            placeholder={
                                isCleverLibrary
                                    ? t`example@school.edu`
                                    : t`email@example.com`
                            }
                            disabled={disableEmail && !!defaultIdentifier}
                            value={identifier || ""}
                            onValidate={(
                                errorMessage?: string | null | undefined,
                            ) => {
                                setIdentifier(errorMessage ? "" : identifier);
                            }}
                            onChange={(newValue) => {
                                setIdentifier(newValue);
                            }}
                            onFocus={() => {
                                setError("");
                            }}
                        />
                    }
                />
                <PasswordTextField
                    testId="password"
                    emptyMessage={t`A password is required to log in.`}
                    label={t`Password`}
                    initialValue={password}
                    onValidate={(errorMessage?: string | null | undefined) => {
                        setPassword(errorMessage ? "" : password);
                    }}
                    onChange={() => {
                        setError("");
                    }}
                />

                <View style={styles.row}>
                    <Button
                        kind="tertiary"
                        size="small"
                        disabled={submitting}
                        onClick={onForgotPasswordClick}
                    >
                        {t`Forgot password?`}
                    </Button>
                    <Spring />
                </View>
                <Button
                    type="submit"
                    disabled={submitting || !identifier || !password}
                    spinner={submitting || ssoIsRedirecting}
                >
                    {t`Log in`}
                </Button>
            </form>
        </>
    );
};

const styles = StyleSheet.create({
    error: {
        color: color.red,
        fontSize: 12,
    },
    formMessage: {
        color: color.offBlack50,
        fontSize: 14,
    },
    row: {
        flexDirection: "row",
    },
});

export default LinkAccountLoginForm;
